import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { isIBAN } from 'validator';
import { useSelector, useDispatch } from 'react-redux';
import { updateAuthUser } from '../../actions/auth';
import { UserService, Alert } from '../../services';
import FormInput from '../FormInput';

const EditUserDetails = ({ userId, setIsEdit, isEdit }) => {
  const dispatch = useDispatch();
  const { user: authUser } = useSelector((state) => state.auth);
  const [userDetails, setUserDetails] = useState(null);
  const validationSchema = Yup.object().shape({
    email: Yup.string().required('Email is required').email('Email is invalid'),
    phone_number: Yup.string().test(
      'is-valid-phone-number',
      'Phone number must contain only numbers',
      (value) => !value || /^\+?\d+$/.test(value),
    ),
    full_name: Yup.string()
      .required('Full name is required')
      .matches(
        /^[^\s]+(\s[^\s]+)+$/,
        'The name should include Firstname and Lastname',
      )
      .max(40),
    business_name: Yup.lazy(() => {
      if (isBusiness) {
        return Yup.string().required('Business name is required');
      } else {
        return Yup.string()
          .transform(() => '')
          .nullable();
      }
    }),
    commerce_number: Yup.lazy(() => {
      if (isBusiness) {
        return Yup.string()
          .required('Commerce number is required')
          .min(2, 'Commerce number must be at least 2 digits')
          .max(100, 'Commerce number must be at most 100 digits')
          .matches(/^\d+$/, 'Commerce number must contain only numbers');
      } else {
        return Yup.number()
          .transform((value, originalValue) =>
            String(originalValue).trim() === '' ? null : value,
          )
          .nullable();
      }
    }),
    legal_entity_identifier: Yup.lazy(() =>
      Yup.string().when('legal_entity_identifier', {
        is: (legal_entity_identifier) => legal_entity_identifier,
        then: () =>
          Yup.string()
            .transform((value, originalValue) =>
              originalValue ? originalValue.toUpperCase() : value,
            )
            .min(
              20,
              'Legal entity identification number must be at least 20 characters',
            )
            .max(
              20,
              'Legal entity identification number must be at most 20 characters',
            ),
      }),
    ),
    postal_code: Yup.lazy(() =>
      Yup.string().when(['address'], {
        is: (address) => address,
        then: () => Yup.string().required('Postal code is required'),
      }),
    ),
    city: Yup.lazy(() =>
      Yup.string()

        .when(['address'], {
          is: (address) => address,
          then: () =>
            Yup.string()
              .required('City is required')
              .max(30, 'City should not contain more than 30 characters'),
        }),
    ),
    address: Yup.lazy(() =>
      Yup.string()

        .max(100, 'Address must contain less than 100 characters')
        .when(['city'], {
          is: (city) => city,
          then: () => Yup.string().required('Address is required'),
        }),
    ),
    account_no: Yup.lazy(() =>
      Yup.string()
        .required('Account number is required')
        .transform((value, originalValue) =>
          originalValue ? originalValue.toUpperCase() : value,
        )
        .matches(
          /^[A-Z0-9]*$/,
          'Account number must contain only uppercase letters and numbers',
        )
        .test('IBAN', 'Account number (IBAN) in-valid', (val) => {
          if (val) {
            return isIBAN(val);
          }
          return true;
        }),
    ),
    account_holder_name: Yup.lazy(() =>
      Yup.string().when('account_no', {
        is: (accountNo) => accountNo,
        then: () =>
          Yup.string()
            .required('Account holder name is required')
            .matches(/^[A-Za-z ]*$/, 'Please enter valid Account Holder Name')
            .min(
              3,
              'Account Holder Name should not contain less than 3 characters',
            )
            .max(
              40,
              'Account Holder Name should not contain more than 40 characters',
            ),
      }),
    ),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    clearErrors,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (userId) {
      getUserDetails(userId);
    }
  }, [userId]);

  const isBusiness = watch('isBusiness');

  useEffect(() => {
    if (!isBusiness) {
      setValue('business_name', '');
      setValue('commerce_number', '');
      setValue('legal_entity_identifier', '');
      clearErrors('business_name');
      clearErrors('commerce_number');
      clearErrors('legal_entity_identifier');
    }
  }, [isBusiness, setValue]);

  const getUserDetails = async (userId) => {
    try {
      let resp = await UserService.getUserDetails(userId);

      if (resp.status === 'failure') {
        Alert.error(resp.message);
        return false;
      }
      setUserDetails(resp?.data);
      const savedValues = {
        full_name: resp?.data.full_name ? resp.data.full_name : '',
        email: resp.data?.email ? resp.data.email : '',
        isBusiness: resp.data?.is_business ? resp.data?.is_business : 0,
        phone_number: resp.data?.phone_number ? resp.data.phone_number : '',
        address: resp.data?.address ? resp.data.address : '',
        business_name: resp.data?.business_name
          ? resp?.data?.business_name
          : '',
        commerce_number: resp.data?.commerce_number
          ? resp?.data?.commerce_number
          : '',
        legal_entity_identifier: resp.data?.legal_entity_identifier
          ? resp?.data?.legal_entity_identifier
          : '',
        postal_code: resp.data?.postal_code ? resp.data.postal_code : '',
        city: resp.data?.city ? resp.data.city : '',
        account_holder_name: resp.data?.userBankDetail?.account_holder_name
          ? resp.data?.userBankDetail?.account_holder_name
          : '',
        account_no: resp.data?.userBankDetail?.account_no
          ? resp.data?.userBankDetail?.account_no
          : '',
      };
      reset(savedValues);
      let _authUser = { ...authUser };
      _authUser['profilePicture'] = resp.data.profile_picture;
      _authUser['userName'] = resp.data.full_name;
      _authUser['bank_details'] = resp.data?.userBankDetail?.account_no
        ? true
        : false;
      _authUser['kyc_status'] = resp.data?.kyc_status;
      _authUser['platform_agreement_status'] =
        resp.data?.platformAgreements?.length > 0 &&
        resp.data?.platformAgreements[0]?.agreement_status;
      dispatch(updateAuthUser(_authUser));
    } catch (error) {
      Alert.error(error.message);
    }
  };

  const copyAccountNumber = () => {
    Alert.success('Account number copied!');
  };

  const updateProfile = async (input) => {
    try {
      let formData = new FormData();

      formData.append('full_name', input.full_name);
      formData.append('email', input.email);
      formData.append('is_business', input?.isBusiness ? 1 : 0);
      formData.append('user_id', userId ? userId : null);
      if (input.business_name) {
        formData.append('business_name', input.business_name);
      }
      if (input.commerce_number) {
        formData.append('commerce_number', input.commerce_number);
      }
      if (input.legal_entity_identifier) {
        formData.append(
          'legal_entity_identifier',
          input.legal_entity_identifier
            ? input.legal_entity_identifier.toUpperCase()
            : '',
        );
      }
      if (input.address) {
        formData.append('address', input.address);
      }
      if (input.account_no) {
        formData.append('account_no', input.account_no.toUpperCase());
      }
      if (input.phone_number) {
        formData.append('phone_number', input.phone_number);
      }
      if (input.postal_code) {
        formData.append('postal_code', input.postal_code);
      }
      if (input.city) {
        formData.append('city', input.city);
      }
      if (input.account_holder_name) {
        formData.append('account_holder_name', input.account_holder_name);
      }
      let resp = await UserService.updateUser(formData);
      if (resp.status === 'failure') {
        Alert.error(resp.message);
        return false;
      }

      Alert.success('User details updated successfully');
      setIsEdit(false);
    } catch (error) {
      Alert.error(error.message);
      return false;
    }
  };

  const handleEntityChange = (e) => {
    const { name, value } = e.target;
    const upperCaseValue = value.toUpperCase();
    setValue(name, upperCaseValue, { shouldValidate: true });
  };

  return (
    <form onSubmit={handleSubmit(updateProfile)}>
      <div className="w-full bg-brandGray rounded-xl p-5 mb-4">
        <div className="xl:grid xl:grid-cols-2 xl:gap-8">
          <div>
            <div className="w-full border-b border-gray-500 mb-4">
              <h4>Personal details</h4>
            </div>
            <div className="mb-4 md:mb-4 flex items-center">
              <FormInput
                type="checkbox"
                name="isBusiness"
                label="Register as a Business"
                register={register}
                disabled={!isEdit}
                value={isBusiness}
              />
            </div>

            <div className="md:grid md:gap-x-4 md:grid-cols-12">
              {isBusiness ? (
                <>
                  <div className="col-span-12">
                    <FormInput
                      label="Business Name"
                      name="business_name"
                      placeholder="Business Name"
                      register={register}
                      errors={errors}
                      required
                      disabled={!isEdit}
                      className={
                        isEdit
                          ? 'bg-[rgb(255 255 255 / 75%)]'
                          : 'bg-transparent border-[#C1C7CB] text-[#002541] pointer-events-none'
                      }
                    />
                  </div>

                  <div className="col-span-6">
                    <FormInput
                      type="text"
                      name="commerce_number"
                      label="Chamber of Commerce Number"
                      placeholder="Chamber of Commerce Number"
                      register={register}
                      errors={errors}
                      disabled={!isEdit}
                      required={true}
                      className={`${
                        isEdit
                          ? 'bg-[rgb(255 255 255 / 75%)]'
                          : 'bg-transparent border-[#C1C7CB] text-[#002541] pointer-events-none'
                      }`}
                    />
                  </div>

                  <div className="col-span-6">
                    <FormInput
                      label="Legal Entity Identification Number"
                      name="legal_entity_identifier"
                      placeholder="506700GE1G29325QX363"
                      register={register}
                      errors={errors}
                      onChange={handleEntityChange}
                      disabled={!isEdit}
                      required
                      className={
                        isEdit
                          ? 'bg-[rgb(255 255 255 / 75%)]'
                          : 'bg-transparent border-[#C1C7CB] text-[#002541] pointer-events-none'
                      }
                    />
                  </div>
                </>
              ) : null}
              <div className="col-span-4">
                <div className="mb-6">
                  <FormInput
                    label={isBusiness ? 'Contact Person' : 'Your Full Name'}
                    name="full_name"
                    placeholder={
                      isBusiness ? 'Contact Person' : 'Your Full Name'
                    }
                    type="text"
                    register={register}
                    errors={errors}
                    required={true}
                    disabled={!isEdit}
                    className={`${
                      isEdit
                        ? 'bg-[rgb(255 255 255 / 75%)] '
                        : 'bg-transparent border-[#C1C7CB] text-[#002541] pointer-events-none'
                    }`}
                  />
                </div>
              </div>

              <div className="col-span-4">
                <div className="mb-6">
                  <FormInput
                    label="Phone Number"
                    name="phone_number"
                    placeholder="Phone Number"
                    type="text"
                    register={register}
                    errors={errors}
                    required={false}
                    disabled={!isEdit}
                    className={`${
                      isEdit
                        ? 'bg-[rgb(255 255 255 / 75%)] '
                        : 'bg-transparent border-[#C1C7CB] text-[#002541] pointer-events-none'
                    }`}
                  />
                </div>
              </div>

              <div className="col-span-4">
                <div className="mb-6">
                  <FormInput
                    label="Email Address"
                    name="email"
                    placeholder="Email Address"
                    type="text"
                    register={register}
                    errors={errors}
                    required={true}
                    disabled={!isEdit}
                    className={`${
                      isEdit
                        ? 'bg-[rgb(255 255 255 / 75%)] '
                        : 'bg-transparent border-[#C1C7CB] text-[#002541] pointer-events-none'
                    }`}
                  />
                </div>
              </div>

              <div className="col-span-5">
                <div className="mb-6">
                  <FormInput
                    label={isBusiness ? 'Business Address' : 'Address'}
                    name="address"
                    placeholder={isBusiness ? 'Business Address' : 'Address'}
                    type="text"
                    register={register}
                    errors={errors}
                    required={true}
                    disabled={!isEdit}
                    className={`${
                      isEdit
                        ? 'bg-[rgb(255 255 255 / 75%)] '
                        : 'bg-transparent border-[#C1C7CB] text-[#002541] pointer-events-none'
                    }`}
                  />
                </div>
              </div>

              <div className="col-span-3">
                <div className="mb-6">
                  <FormInput
                    label="Postal code"
                    name="postal_code"
                    placeholder="Postal code"
                    type="text"
                    register={register}
                    errors={errors}
                    required={true}
                    disabled={!isEdit}
                    className={`${
                      isEdit
                        ? 'bg-[rgb(255 255 255 / 75%)] '
                        : 'bg-transparent border-[#C1C7CB] text-[#002541] pointer-events-none'
                    }`}
                  />
                </div>
              </div>

              <div className="col-span-4">
                <div className="mb-6">
                  <FormInput
                    label="City"
                    name="city"
                    placeholder="City"
                    type="text"
                    register={register}
                    errors={errors}
                    required={true}
                    disabled={!isEdit}
                    className={`${
                      isEdit
                        ? 'bg-[rgb(255 255 255 / 75%)] '
                        : 'bg-transparent border-[#C1C7CB] text-[#002541] pointer-events-none'
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="w-full border-b border-gray-500 mb-4">
              <h4>Bank details</h4>
            </div>

            <div className="xl:mt-[52px]">
              <div>
                <div className="mb-6 relative">
                  <FormInput
                    label="Account Number (IBAN)"
                    name="account_no"
                    placeholder="Account Number (IBAN)"
                    type="text"
                    register={register}
                    errors={errors}
                    required={true}
                    disabled={!isEdit}
                    valueToCopy={userDetails?.userBankDetail?.account_no || ''}
                    showCopy={!isEdit}
                    onCopy={copyAccountNumber}
                    onChange={handleEntityChange}
                    className={`${
                      isEdit
                        ? 'bg-[rgb(255 255 255 / 75%)]'
                        : 'bg-transparent border-[#C1C7CB] text-[#002541] pointer-events-none'
                    }`}
                  />
                </div>
              </div>

              <div>
                <div className="mb-6">
                  <FormInput
                    label="In the name of"
                    name="account_holder_name"
                    placeholder="In the name of"
                    type="text"
                    register={register}
                    errors={errors}
                    required={true}
                    disabled={!isEdit}
                    className={`${
                      isEdit
                        ? 'bg-[rgb(255 255 255 / 75%)]'
                        : 'bg-transparent border-[#C1C7CB] text-[#002541] pointer-events-none'
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>
          {isEdit && (
            <div className="col-span-2 text-right">
              <div className="inline-flex gap-3">
                <button
                  type="button"
                  className="bg-[#AAAAAA] font-semibold text-white border border-[#AAAAAA] whitespace-nowrap inline-flex items-center text-sm mb-3 md:mb-0"
                  onClick={() => setIsEdit(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-[#99D94A] font-semibold text-brandBlue border border-[#99D94A] whitespace-nowrap inline-flex items-center text-sm mb-3 md:mb-0"
                >
                  Update Profile
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

export default EditUserDetails;
